'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';

export default function HeroSection() {
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef?.current) {
        const scrollY = window.scrollY;
        parallaxRef.current.style.transform = `translateY(${scrollY * 0.4}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-navy-dark">
      {/* Parallax Background */}
      <div ref={parallaxRef} className="absolute inset-0 scale-110">
        <Image
          src="/assets/images/regent_seven_seas_hero.png"
          alt="Regent Seven Seas luxury cruise ship sailing on open waters"
          fill
          priority
          quality={85}
          sizes="100vw"
          className="object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/90 via-navy-dark/70 to-navy-dark/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-transparent to-navy-dark/30" />
      </div>
      {/* Floating decorative elements */}
      <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-gold/5 blur-3xl animate-float" />
      <div className="absolute bottom-1/3 right-1/3 w-96 h-96 rounded-full bg-primary/5 blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      {/* Grain texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8 animate-fade-in">
            <div className="h-px w-12 bg-gold" />
            <span className="text-gold text-xs tracking-[0.3em] uppercase font-light">
              Australia's Exclusive Cruise Membership
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-white mb-6 leading-[1.1]">
            <span className="block text-5xl md:text-7xl font-light">
              Sail Beyond
            </span>
            <span className="block text-5xl md:text-7xl font-light italic text-gold">
              the Ordinary
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-white/60 text-lg md:text-xl font-light leading-relaxed mb-10 max-w-xl">
            Join the Ultra Luxury Cruise Club and receive an <span className="text-gold font-medium">exclusive members only discount</span> off every ultra luxury cruise — exclusively for our members.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#join"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gold-gradient text-navy-dark font-medium text-sm tracking-[0.12em] uppercase rounded-sm hover:shadow-2xl hover:shadow-gold/30 transition-all duration-300 hover:-translate-y-0.5"
            >
              Become a Member
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <a
              href="https://luxury-travel-club.cruiseco.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 px-8 py-4 border border-white/20 text-white font-light text-sm tracking-[0.12em] uppercase rounded-sm hover:border-gold/50 hover:text-gold transition-all duration-300"
            >
              Explore Cruises
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          {/* Stats */}
          <div className="mt-16 flex gap-10 border-t border-white/10 pt-10">
            {[
              { value: 'Exclusive', label: 'Member Discount' },
              { value: '100+', label: 'Cruise Destinations' },
              { value: 'Elite', label: 'Luxury Lines' },
            ]?.map((stat) => (
              <div key={stat?.label}>
                <div className="font-serif text-3xl text-gold font-light">{stat?.value}</div>
                <div className="text-white/40 text-xs tracking-[0.15em] uppercase mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/30 text-xs tracking-[0.2em] uppercase">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>
  );
}
